<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('tax.title') }}</h1>
      <p>{{ $t('tax.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="form-section">
        <div class="form-row">
          <label>{{ $t('tax.grossSalary') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="salary" placeholder="">
            <span class="unit">{{ $t('tax.unit') }}</span>
          </div>
        </div>

        <div class="form-row">
          <label>{{ $t('tax.insurance') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="insurance" placeholder="">
            <span class="unit">{{ $t('tax.unit') }}</span>
          </div>
          <div class="tip">{{ $t('tax.insuranceTip') }}</div>
        </div>

        <div class="form-row">
          <label>{{ $t('tax.specialDeduction') }}</label>
          <div class="input-group">
            <input type="number" v-model.number="specialDeduction" placeholder="">
            <span class="unit">{{ $t('tax.unit') }}</span>
          </div>
          <div class="deduction-items">
            <label class="checkbox-label">
              <input type="checkbox" v-model="deductions.children" @change="updateSpecialDeduction">
              <span>{{ $t('tax.deductions.children') }}</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="deductions.education" @change="updateSpecialDeduction">
              <span>{{ $t('tax.deductions.education') }}</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="deductions.medical" @change="updateSpecialDeduction">
              <span>{{ $t('tax.deductions.medical') }}</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="deductions.housing" @change="updateSpecialDeduction">
              <span>{{ $t('tax.deductions.housing') }}</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="deductions.rent" @change="updateSpecialDeduction">
              <span>{{ $t('tax.deductions.rent') }}</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="deductions.elderly" @change="updateSpecialDeduction">
              <span>{{ $t('tax.deductions.elderly') }}</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="deductions.baby" @change="updateSpecialDeduction">
              <span>{{ $t('tax.deductions.baby') }}</span>
            </label>
          </div>
        </div>

        <button class="btn primary calculate-btn" @click="calculate">{{ $t('tax.calculate') }}</button>
      </div>

      <div class="result-section" v-if="result">
        <h3>{{ $t('tax.results') }}</h3>
        
        <div class="result-cards">
          <div class="result-card">
            <div class="card-label">{{ $t('tax.grossSalary') }}</div>
            <div class="card-value">¥ {{ formatMoney(result.grossSalary) }}</div>
          </div>
          <div class="result-card highlight-red">
            <div class="card-label">{{ $t('tax.taxPayable') }}</div>
            <div class="card-value">¥ {{ formatMoney(result.tax) }}</div>
          </div>
          <div class="result-card highlight">
            <div class="card-label">{{ $t('tax.netSalary') }}</div>
            <div class="card-value">¥ {{ formatMoney(result.netSalary) }}</div>
          </div>
        </div>

        <div class="detail-section">
          <h4>{{ $t('tax.details') }}</h4>
          <div class="detail-list">
            <div class="detail-item">
              <span class="detail-label">{{ $t('tax.grossSalary') }}</span>
              <span class="detail-value">{{ formatMoney(result.grossSalary) }} {{ $t('tax.unit') }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">- {{ $t('tax.insurance') }}</span>
              <span class="detail-value">{{ formatMoney(result.insurance) }} {{ $t('tax.unit') }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">- {{ $t('tax.threshold') }}</span>
              <span class="detail-value">5,000.00 {{ $t('tax.unit') }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">- {{ $t('tax.specialDeduction') }}</span>
              <span class="detail-value">{{ formatMoney(result.specialDeduction) }} {{ $t('tax.unit') }}</span>
            </div>
            <div class="detail-item highlight">
              <span class="detail-label">= {{ $t('tax.taxableIncome') }}</span>
              <span class="detail-value">{{ formatMoney(result.taxableIncome) }} {{ $t('tax.unit') }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('tax.taxRate') }}</span>
              <span class="detail-value">{{ result.taxRate }}%</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('tax.quickDeduction') }}</span>
              <span class="detail-value">{{ formatMoney(result.quickDeduction) }} {{ $t('tax.unit') }}</span>
            </div>
            <div class="detail-item highlight">
              <span class="detail-label">= {{ $t('tax.taxPayable') }}</span>
              <span class="detail-value text-red">{{ formatMoney(result.tax) }} {{ $t('tax.unit') }}</span>
            </div>
          </div>
        </div>

        <div class="tax-table-section">
          <h4>{{ $t('tax.taxTable') }}</h4>
          <table class="tax-table">
            <thead>
              <tr>
                <th>{{ $t('tax.level') }}</th>
                <th>{{ $t('tax.taxableRange') }}</th>
                <th>{{ $t('tax.taxRate') }}</th>
                <th>{{ $t('tax.quickDeduction') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in taxRates" :key="index" 
                :class="{ active: result.taxLevel === index + 1 }">
                <td>{{ index + 1 }}</td>
                <td>{{ item.range }}</td>
                <td>{{ item.rate }}%</td>
                <td>{{ item.deduction }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('tax.whatIsTax') }}</h2>
      <p>{{ $t('tax.whatIsTaxDesc') }}</p>

      <h2>{{ $t('tax.taxFormula') }}</h2>
      <p>{{ $t('tax.taxFormulaDesc') }}</p>

      <h2>{{ $t('tax.specialDeductionsExplain') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('tax.specialDeductionsItems')" :key="'ded'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('tax.taxRateLevels') }}</h2>
      <p>{{ $t('tax.taxRateLevelsDesc') }}</p>

      <h2>{{ $t('tax.yearEndBonus') }}</h2>
      <p>{{ $t('tax.yearEndBonusDesc') }}</p>

      <h2>{{ $t('tax.taxPlanningTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('tax.taxPlanningTipsItems')" :key="'plan'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('tax.annualSettlement') }}</h2>
      <p>{{ $t('tax.annualSettlementDesc') }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TaxCalculator',
  data() {
    return {
      salary: 15000,
      insurance: 2000,
      specialDeduction: 0,
      deductions: {
        children: false,
        education: false,
        medical: false,
        housing: false,
        rent: false,
        elderly: false,
        baby: false
      },
      result: null,
      taxRates: [
        { range: '不超过3,000元', rate: 3, deduction: 0 },
        { range: '超过3,000元至12,000元', rate: 10, deduction: 210 },
        { range: '超过12,000元至25,000元', rate: 20, deduction: 1410 },
        { range: '超过25,000元至35,000元', rate: 25, deduction: 2660 },
        { range: '超过35,000元至55,000元', rate: 30, deduction: 4410 },
        { range: '超过55,000元至80,000元', rate: 35, deduction: 7160 },
        { range: '超过80,000元', rate: 45, deduction: 15160 }
      ]
    }
  },
  methods: {
    updateSpecialDeduction() {
      let total = 0
      if (this.deductions.children) total += 2000
      if (this.deductions.education) total += 400
      if (this.deductions.housing) total += 1000
      if (this.deductions.rent) total += 1500
      if (this.deductions.elderly) total += 3000
      if (this.deductions.baby) total += 2000
      this.specialDeduction = total
    },

    calculate() {
      const threshold = 5000 // 起征点
      
      // 应纳税所得额 = 税前工资 - 五险一金 - 起征点 - 专项附加扣除
      const taxableIncome = Math.max(0, this.salary - this.insurance - threshold - this.specialDeduction)
      
      // 根据税率表计算
      let tax = 0
      let taxRate = 0
      let quickDeduction = 0
      let taxLevel = 0

      if (taxableIncome <= 0) {
        tax = 0
        taxRate = 0
        taxLevel = 0
      } else if (taxableIncome <= 3000) {
        taxRate = 3
        quickDeduction = 0
        taxLevel = 1
      } else if (taxableIncome <= 12000) {
        taxRate = 10
        quickDeduction = 210
        taxLevel = 2
      } else if (taxableIncome <= 25000) {
        taxRate = 20
        quickDeduction = 1410
        taxLevel = 3
      } else if (taxableIncome <= 35000) {
        taxRate = 25
        quickDeduction = 2660
        taxLevel = 4
      } else if (taxableIncome <= 55000) {
        taxRate = 30
        quickDeduction = 4410
        taxLevel = 5
      } else if (taxableIncome <= 80000) {
        taxRate = 35
        quickDeduction = 7160
        taxLevel = 6
      } else {
        taxRate = 45
        quickDeduction = 15160
        taxLevel = 7
      }

      if (taxableIncome > 0) {
        tax = taxableIncome * taxRate / 100 - quickDeduction
      }

      this.result = {
        grossSalary: this.salary,
        insurance: this.insurance,
        specialDeduction: this.specialDeduction,
        taxableIncome,
        taxRate,
        quickDeduction,
        tax: Math.max(0, tax),
        netSalary: this.salary - this.insurance - Math.max(0, tax),
        taxLevel
      }
    },

    formatMoney(value) {
      return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 900px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
}

.form-section {
  margin-bottom: 32px;
}

.form-row {
  margin-bottom: 20px;
}

.form-row > label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.input-group {
  display: flex;
  align-items: center;
}

.input-group input {
  flex: 1;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 16px;
}

.input-group input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.input-group .unit {
  margin-left: 12px;
  color: var(--text-secondary);
  font-size: 14px;
}

.tip {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 6px;
}

.deduction-items {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 12px;
}

@media (max-width: 600px) {
  .deduction-items {
    grid-template-columns: 1fr;
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--text-secondary);
}

.checkbox-label input {
  width: 16px;
  height: 16px;
  accent-color: var(--accent-color);
}

.calculate-btn {
  width: 100%;
  padding: 14px;
  font-size: 16px;
  margin-top: 12px;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn.primary {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.btn.primary:hover {
  background: var(--accent-hover);
}

.result-section h3 {
  font-size: 20px;
  margin-bottom: 20px;
  color: var(--text-primary);
}

.result-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}

@media (max-width: 600px) {
  .result-cards {
    grid-template-columns: 1fr;
  }
}

.result-card {
  background: var(--bg-tertiary);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.result-card.highlight {
  background: var(--accent-light);
  border: 1px solid var(--accent-color);
}

.result-card.highlight-red {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.card-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.card-value {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
}

.result-card.highlight .card-value {
  color: var(--accent-color);
}

.result-card.highlight-red .card-value {
  color: var(--error-color);
}

.detail-section {
  margin-bottom: 32px;
}

.detail-section h4,
.tax-table-section h4 {
  font-size: 16px;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.detail-list {
  background: var(--bg-tertiary);
  border-radius: 12px;
  padding: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-color);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item.highlight {
  background: var(--accent-light);
  margin: 8px -16px;
  padding: 12px 16px;
  border-radius: 8px;
  border-bottom: none;
}

.detail-label {
  color: var(--text-secondary);
  font-size: 14px;
}

.detail-value {
  color: var(--text-primary);
  font-weight: 500;
}

.detail-value.text-red {
  color: var(--error-color);
}

.tax-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.tax-table th,
.tax-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
}

.tax-table th {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-weight: 500;
}

.tax-table td {
  color: var(--text-primary);
}

.tax-table tr.active {
  background: var(--accent-light);
}

.tax-table tr.active td {
  color: var(--accent-color);
  font-weight: 500;
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}
</style>
